import { useState, useEffect } from 'react';
import { Select, Flex, Button, Text, Image } from '../../../2022-component-library';
import { useTestContext } from '../../../context/testContext';
import { useCalibration } from '../../../context/calibrationContext';
import {
  refractionStimulusBackgrounds,
  refractionStimulusColorOptions,
  refractionStimulusSizeOptions,
  refractionOptotypeOptions,
  ibisVisionToolOptions,
  calculateRefractionResult,
  colorFilters,
} from '../../../helpers/refraction';
import { useReceiveMessage, usePostMessage } from '../../../context/messageContext';
import styled from '@emotion/styled';
import { kaysOptotypes } from '../../../helpers/kaysOptotypes';

const Separator = styled(Flex)`
  background-color: #aab2b2;
  width: 1px;
  height: 90%;
`;

const ResponsiveRefractionTestOptician = ({ eyeTested, setCanProgress }) => {
  const { testData, updateTestData } = useTestContext();
  const postMessage = usePostMessage();
  const {
    calibrationData: { pixelDensity },
  } = useCalibration();

  const [stimulusBackground, setStimulusBackground] = useState(null);
  const [stimulusType, setStimulusType] = useState(null);
  const [stimulusSize, setStimulusSize] = useState(null);
  const [stimulusColor, setStimulusColor] = useState(null);
  const [selectedOptotype, setSelectedOptotype] = useState(null);
  const [visibleStimulus, setVisibleStimulus] = useState(true);
  const [positionData, setPositionData] = useState({
    1: {
      distance: null,
      tracking: false,
    },
    2: {
      distance: null,
      tracking: false,
    },
    3: {
      distance: null,
      tracking: false,
    },
  });

  const messageFromPatient = useReceiveMessage();

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const getStimulusBackgroundObject = (selectedStimulusBackground) => {
    return refractionStimulusBackgrounds.find((element) => element.value === selectedStimulusBackground);
  };

  const startDetections = (positionIndex) => {
    setPositionData({
      ...positionData,
      [positionIndex]: {
        ...positionData[positionIndex],
        tracking: true,
      },
    });

    postMessage({ type: 'detectDistance' });
  };

  const stopDetections = (positionIndex) => {
    postMessage({ type: 'stopDetections' });
    setPositionData({
      ...positionData,
      [positionIndex]: { ...positionData[positionIndex], tracking: false },
    });
  };

  const sendNewTarget = () => {
    postMessage({
      type: 'updateTarget',
      content: {
        stimulusBackground: getStimulusBackgroundObject(stimulusBackground),
        stimulusColor: stimulusColor,
        stimulusSize: stimulusSize,
        stimulusType: stimulusType,
        stimulusOptotype: selectedOptotype,
        visibleStimulus: visibleStimulus,
        showAstigmatismClock: false,
        pixelDensity: pixelDensity,
      },
    });
  };

  const distanceCollectionButtons = (positionIndex) => {
    return (
      <Flex flexDirection='column'>
        <Text fontSize='0.875rem' marginBottom='0.5rem' textAlign='center'>
          {`Position ${positionIndex}: (${positionData[positionIndex]?.distance || '--'}mm)`}
        </Text>
        <Flex flexDirection='row'>
          <Button
            disabled={Object.keys(positionData).find((element) => positionData[element].tracking)}
            onClick={() => {
              startDetections(positionIndex);
            }}
            width='45%'
            margin='0px 5px'
          >
            Track
          </Button>
          <Button
            disabled={!positionData[positionIndex].tracking}
            onClick={() => {
              stopDetections(positionIndex);
            }}
            width='45%'
            margin='0px 5px'
          >
            Capture
          </Button>
        </Flex>
      </Flex>
    );
  };

  useEffect(() => {
    if (messageFromPatient.type === 'patientDistance') {
      for (let i = 1; i <= 3; i++) {
        if (positionData[i].tracking === true) {
          setPositionData({
            ...positionData,
            [i]: {
              ...positionData[i],
              distance: messageFromPatient.content,
            },
          });
        }
      }
    }
  }, [messageFromPatient]);

  useEffect(() => {
    if (stimulusBackground === null && stimulusColor === null && stimulusSize === null && stimulusType === null) {
      postMessage({ type: 'initialiseDetection', content: ibisVisionToolOptions[0].values });
      setStimulusType('optotype');
      setStimulusColor('yellow');
      setStimulusBackground('landscape2');
      setStimulusSize('8');
      setSelectedOptotype('house');
    } else {
      sendNewTarget();
    }
  }, [stimulusBackground, stimulusColor, stimulusSize, stimulusType, selectedOptotype, visibleStimulus]);

  useEffect(() => {
    if (positionData[1].distance !== null && positionData[2].distance !== null && positionData[3].distance !== null) {
      setCanProgress(true);
      const results = calculateRefractionResult(positionData[1].distance, positionData[2].distance, positionData[3].distance);
      updateTestData({ [eyeTested]: { ...testData[eyeTested], ...{ score: results } } });
    } else {
      setCanProgress(false);
    }
  }, [positionData, eyeTested, updateTestData]);

  return (
    <Flex column alignItems='center' padding='1.5rem 1rem 0 1rem' width='90vw' maxWidth='720px'>
      <Flex justifyContent='space-between' width='90%' marginBottom='1rem'>
        <Flex
          column
          centered
          border='1px solid black'
          borderRadius='4px'
          width='70%'
          height='45vh'
          maxHeight='250px'
          maxWidth='350px'
          padding='0.5rem'
        >
          <Flex>
            <Flex width='33%' centered>
              <Select
                width='100%'
                label='Background Content'
                value={stimulusBackground}
                onChange={(e) => {
                  setStimulusBackground(e.target.value);
                }}
              >
                {refractionStimulusBackgrounds.map((stimulusBackground) => {
                  return (
                    <option key={`test-option-${stimulusBackground.value}`} value={stimulusBackground.value}>
                      {stimulusBackground.label}
                    </option>
                  );
                })}
              </Select>
            </Flex>
            <Flex width='33%'>
              <Select
                marginRight='1rem'
                label='Stimulus Color'
                value={stimulusColor}
                onChange={(e) => {
                  setStimulusColor(e.target.value);
                }}
              >
                {refractionStimulusColorOptions.map((stimulusColor) => {
                  return (
                    <option key={`test-option-${stimulusColor.value}`} value={stimulusColor.value}>
                      {stimulusColor.label}
                    </option>
                  );
                })}
              </Select>
            </Flex>
            <Flex width='33%'>
              <Select
                label='Stimulus Size'
                value={stimulusSize}
                onChange={(e) => {
                  setStimulusSize(e.target.value);
                }}
              >
                {refractionStimulusSizeOptions.map((stimulusSize) => {
                  return (
                    <option key={`test-option-${stimulusSize.value}`} value={stimulusSize.value}>
                      {stimulusSize.label}
                    </option>
                  );
                })}
              </Select>
            </Flex>
          </Flex>

          <Flex>
            <Flex width='33%'>
              <Select
                label='Display Stimulus'
                value={visibleStimulus ? 'yes' : 'no'}
                onChange={(e) => {
                  setVisibleStimulus(e.target.value === 'yes' ? true : false);
                }}
              >
                {[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ].map((type) => {
                  return <option value={type.value}>{type.label}</option>;
                })}
              </Select>
            </Flex>
            <Flex width='33%'>
              <Select
                label='Stimulus type'
                value={stimulusType}
                onChange={(e) => {
                  setStimulusType(e.target.value);
                }}
              >
                {['Dot', 'Optotype'].map((type) => {
                  return (
                    <option key={`test-option-${type.toLowerCase()}`} value={type.toLowerCase()}>
                      {type}
                    </option>
                  );
                })}
              </Select>
            </Flex>
            {stimulusType === 'optotype' ? (
              <Flex mb='10px' flexDirection='column'>
                <Select
                  label='Optotype'
                  value={selectedOptotype}
                  onChange={(e) => {
                    setSelectedOptotype(e.target.value);
                  }}
                >
                  {refractionOptotypeOptions.map((opt) => {
                    return (
                      <option key={`test-option-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </option>
                    );
                  })}
                </Select>
              </Flex>
            ) : (
              ''
            )}
          </Flex>
        </Flex>

        <Flex
          column
          centered
          border='1px solid black'
          borderRadius='4px'
          width='30%'
          height='45vh'
          maxHeight='250px'
          maxWidth='350px'
          padding='0.5rem'
        >
          <Text fontSize='0.75rem'>Preview</Text>
          <Flex
            marginTop='0.5rem'
            justifyContent='center'
            style={{ border: '1px solid black', position: 'relative', backgroundColor: stimulusBackground === 'black' ? 'black' : 'white' }}
            height='60%'
            width='100%'
          >
            {stimulusBackground && getStimulusBackgroundObject(stimulusBackground).media ? (
              <Image src={getStimulusBackgroundObject(stimulusBackground).media} width='100%' />
            ) : null}
            <Flex
              style={{
                display: visibleStimulus ? 'block' : 'none',
                position: 'absolute',
                top: '10px',
                backgroundColor: stimulusType === 'dot' ? '#000' : 'transparent',
                filter: colorFilters[stimulusColor],
                borderRadius: '50%',
                width: stimulusType === 'dot' ? '10px' : '20px',
                height: stimulusType === 'dot' ? '10px' : '20px',
              }}
            >
              {stimulusType === 'optotype' && kaysOptotypes[selectedOptotype]}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Flex height='15vh' maxHeight='75px' centered marginBottom='1rem' maxWidth='95vw'>
        {distanceCollectionButtons(1)}
        <Separator />
        {distanceCollectionButtons(2)}
        <Separator />
        {distanceCollectionButtons(3)}
      </Flex>
    </Flex>
  );
};

export default ResponsiveRefractionTestOptician;
